import React, { useEffect, useState } from 'react'

import {
  fetchViewPreference,
  updateViewPreference,
  type GridViewPreference,
} from '../../../../api/userPreferencesApi'
import { ViewPreferenceDialog } from '@/features/grid/components/ViewPreferenceDialog'
import {
  GRID_VIEW_MODE_STORAGE_KEY,
  storeGridViewPreference,
} from '@/features/grid/utils/gridViewPreference'

type ViewPreferenceGateProps = {
  userId?: string | number | null
}

export function ViewPreferenceGate({ userId }: ViewPreferenceGateProps) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setOpen(false)
      return
    }

    let active = true
    setError(null)

    void fetchViewPreference(userId)
      .then(preference => {
        if (!active) return

        const preferredView = preference.data?.view
        if (preferredView === 'matter' || preferredView === 'time') {
          // The creation preference controls the grid view on every load.
          localStorage.removeItem(GRID_VIEW_MODE_STORAGE_KEY)
          storeGridViewPreference(preferredView)
          setOpen(false)
          return
        }

        setOpen(Boolean(preference.requires_selection))
      })
      .catch(fetchError => {
        console.error('Failed to load view preference', fetchError)
        if (active && (fetchError as any)?.response?.status === 404) {
          setOpen(true)
        }
      })

    return () => {
      active = false
    }
  }, [userId])

  const handleSelect = async (view: GridViewPreference) => {
    if (!userId || saving) return

    setSaving(true)
    setError(null)
    try {
      await updateViewPreference(userId, view)
      storeGridViewPreference(view)
      setOpen(false)
    } catch (saveError) {
      console.error('Failed to save view preference', saveError)
      setError('Unable to save your view preference. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <ViewPreferenceDialog
      isSaving={saving}
      error={error}
      onSelect={handleSelect}
    />
  )
}
